import { useCallback, useState } from 'react';
import { pushRegistrationEventToDataLayer } from '../../common/analytics';
import { postRegisterUser } from '../../common/fetch';
import { convertToCommonErrors } from '../../common/utils/errorUtils.js';
import { dispatchAuthAction } from '../../selfservice/actions/authSideEffectActions.js';
import type { CommonError } from '../../common/types/errors.js';
import type { Company, RegisterUserRequestUserDetails } from '../../generated/api/models.js';

interface RegistrationState {
  registrationInProgress: boolean;
  registrationCompleted: boolean;
  isInExternalSystem: boolean;
  errors: CommonError[];
}

export const useRegistration = () => {
  const [state, setState] = useState<RegistrationState>({
    registrationInProgress: false,
    registrationCompleted: false,
    isInExternalSystem: false,
    errors: [],
  });

  const registerUser = useCallback(
    async (identityCertificate: string, company: Company, userDetails: RegisterUserRequestUserDetails) => {
      setState(prev => ({
        ...prev,
        registrationInProgress: true,
        errors: [],
        isInExternalSystem: false,
      }));

      try {
        await postRegisterUser({
          identityCertificate,
          businessId: company.businessId,
          companyName: company.name,
          companyAddress: {
            line1: company.postalAddress!,
            postalCode: company.postCode!,
            postOffice: company.postOffice!,
            countryCode: company.countryCode!,
          },
          userDetails,
        });

        sessionStorage.setItem('registration-user-credentials', JSON.stringify({ email: userDetails.email }));

        pushRegistrationEventToDataLayer(true);

        if (userDetails.email && userDetails.password) {
          dispatchAuthAction('LOGIN', userDetails.email, userDetails.password);
        }

        setState(prev => ({
          ...prev,
          errors: [],
          registrationInProgress: false,
          registrationCompleted: true,
        }));
      } catch (error) {
        pushRegistrationEventToDataLayer(false);

        setState(prev => ({
          ...prev,
          registrationInProgress: false,
          errors: convertToCommonErrors(`ajax error ${error.status}`, error.status),
          isInExternalSystem: error.status === 409,
        }));
      }
    },
    []
  );

  return {
    ...state,
    registerUser,
  };
};
